import ArrowLeftIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/ArrowLeft.js';
import TrashIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Trash.js';
import { PageHeader as ModernPageHeader } from '@diligentcorp/atlas-theme-mui/lib/themes/lens/components.js';
import { Box, Button as MuiButton, IconButton } from '@mui/material';
import React from 'react';
import { Button, Spinner } from 'src/components/atoms';
import { AtlasMuiThemeProvider } from 'src/theme/atlas/atlasMuiThemeProvider';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import {
  CaseScreenBackButton,
  Modal,
  PageHeaderWithActions,
} from 'src/components/molecules';
import {
  ActionContainer,
  DraftHeaderMain,
  DraftHeaderRow,
} from './DraftCaseHeader.styles';

interface DraftCaseHeaderProps {
  isExistingReport: boolean;
  onBack: () => void;
  onSaveDraft: () => void;
  onDelete: () => void;
  onAuditLog?: () => void;
  saveButtonDisabled?: boolean;
  saveButtonLoading?: boolean;
  isModernized?: boolean;
}

export const DraftCaseHeader = ({
  isExistingReport,
  onBack,
  onSaveDraft,
  onDelete,
  onAuditLog,
  saveButtonDisabled = false,
  saveButtonLoading = false,
  isModernized = false,
}: DraftCaseHeaderProps) => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const header = isExistingReport ? 'Add existing case' : 'Add new case';
  const modernizedHeader = 'Adding a case';
  const subtitle =
    'Liaise with reporter and enter the following details to manually submit on their behalf';

  const deleteConfirmationModal = (
    <Modal
      open={showDeleteConfirmation}
      setOpen={setShowDeleteConfirmation}
      size="medium"
      title="Delete case?"
      description="Are you sure you want to delete this report? You will not be able to recover the report after deletion."
      confirmButtonText="Delete case"
      cancelButtonText="Keep case"
      loading={deleteLoading}
      onCancel={() => setShowDeleteConfirmation(false)}
      onConfirm={() => {
        setDeleteLoading(true);
        onDelete();
      }}
      data-testid="delete-confirmation-modal"
    />
  );

  return (
    <>
      {isModernized ? (
        <AtlasMuiThemeProvider>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}
            >
              <IconButton
                onClick={onBack}
                data-testid="draft-case-back-button"
                aria-label="Back"
              >
                <ArrowLeftIcon />
              </IconButton>
              <ModernPageHeader
                pageTitle={modernizedHeader}
                pageSubtitle={subtitle}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MuiButton
                variant="text"
                color="primary"
                size="medium"
                data-testid="audit-log-button"
                disabled={!onAuditLog}
                onClick={() => onAuditLog?.()}
              >
                Audit log
              </MuiButton>
              <MuiButton
                variant="outlined"
                size="medium"
                data-testid="delete-draft-button"
                startIcon={<TrashIcon aria-hidden />}
                onClick={() => setShowDeleteConfirmation(true)}
              >
                Delete draft
              </MuiButton>
            </Box>
          </Box>
          {deleteConfirmationModal}
        </AtlasMuiThemeProvider>
      ) : (
        <>
          <DraftHeaderRow>
            <CaseScreenBackButton
              onClick={onBack}
              data-testid="draft-case-back-button"
            />
            <DraftHeaderMain>
              <PageHeaderWithActions
                title={header}
                description={subtitle}
                $minHeight="auto"
                customActions={
                  <ActionContainer>
                    <Button
                      variant="secondary"
                      data-testid="delete-draft-button"
                      onClick={() => setShowDeleteConfirmation(true)}
                    >
                      <StyledIconInButton
                        size="medium"
                        position="left"
                        className="uil uil-trash-alt"
                      />
                      Delete
                    </Button>
                    <Button
                      variant="secondary"
                      data-testid="save-draft-button"
                      onClick={onSaveDraft}
                      disabled={saveButtonDisabled}
                      loading={saveButtonLoading}
                    >
                      {!saveButtonLoading && (
                        <StyledIconInButton
                          size="medium"
                          position="left"
                          className="uil uil-save"
                        />
                      )}
                      {saveButtonLoading && (
                        <Spinner
                          size="small"
                          variant="tertiary"
                          data-testid="save-draft-spinner"
                        />
                      )}
                      Save draft
                    </Button>
                    <Button
                      variant="secondary"
                      data-testid="audit-log-button"
                      disabled={!onAuditLog}
                      onClick={() => onAuditLog?.()}
                    >
                      Audit log
                    </Button>
                  </ActionContainer>
                }
              />
            </DraftHeaderMain>
          </DraftHeaderRow>
          {deleteConfirmationModal}
        </>
      )}
    </>
  );
};
